import React, {
  FunctionComponent,
  SyntheticEvent,
  useContext,
  useRef,
  useState,
} from "react";
import {
  Button,
  Choice,
  ChoiceList,
  CloseIconThin,
  HelpDrawer,
  SkipNav,
} from "@cmsgov/ds-medicare-gov";
import {
  FormattedResultsMessage,
  InNetworkHelperDrawer,
  PharmacyCard,
  SimplePharamcyNetworkStatus,
} from "./";
import {
  CONSTANTS,
  IntlContext,
  sendTealiumEvent,
  useTranslate,
} from "../../../helpers";
import filterIcon from "./../../../assets/filter.svg";
import mapIcon from "./../../../assets/map.svg";
import { Pharmacy } from "../../../@types";
import { PharmacyResultsType, ToggledFrom } from "../types";
import { normalizeStringCase } from "../helpers";
import Pagination from "../../../components/Pagination";
import PharmacySelectionMap from "../../../components/PharmacySelectionMap";
import { CCXPDropdown } from "../../../components";
import { labelIdSuffix, nameIdSuffix } from "./constants";

export const ResultsContainer: FunctionComponent<{
  handlePreferredOnlyClick: (showPreferred: boolean) => void;
  loadPharmacies: (params: {
    radius_miles?: number;
    pageNumber?: number;
    calculateAverageDistance?: boolean;
    filterByPreferred?: boolean;
  }) => Promise<void>;
  setFooterMessage: (message: string) => void;
  setMobileSearchDistance: (mobileSearchDistance: number) => void;
  setPage: (page: number) => void;
  setSearchDistance: (distance: number) => void;
  setShowMap: (shouldShowMap: boolean) => void;
  setShowMobileFilter: (shouldShowMobileFilter: boolean) => void;
  toggleMailOrderPharmacy: (isMailOrder: boolean) => void;
  togglePharmacy: (
    toggledFrom?: ToggledFrom
  ) => (npi: string | number, pharmacyNumber: number) => void;
  isInNetwork: boolean;
  isMailOrderInNetwork: boolean;
  isMailOrderPreferred: boolean;
  loading: boolean;
  mailOrder: boolean;
  mobileSearchDistance: number | undefined;
  page: number;
  pharmacies: Pharmacy[];
  pharmacyAnchorTarget: string;
  pharmacyResults: PharmacyResultsType | undefined;
  previousNameSearch: string;
  searchDistance: number;
  showMap: boolean;
  showMobileFilter: boolean;
  shouldMailOrderBeInResults: boolean;
  totalPages: number;
  totalPharmacies: number;
  inNetworkFilterAnalytics?: (
    showPreferred: boolean,
    searchDistance: number
  ) => void;
}> = ({
  handlePreferredOnlyClick,
  isInNetwork,
  isMailOrderInNetwork,
  isMailOrderPreferred,
  loading,
  loadPharmacies,
  mailOrder,
  mobileSearchDistance,
  page,
  pharmacies,
  pharmacyAnchorTarget,
  pharmacyResults,
  previousNameSearch,
  searchDistance,
  setFooterMessage,
  setMobileSearchDistance,
  setPage,
  setSearchDistance,
  setShowMap,
  setShowMobileFilter,
  showMap,
  showMobileFilter,
  shouldMailOrderBeInResults,
  toggleMailOrderPharmacy,
  togglePharmacy,
  totalPages,
  totalPharmacies,
  inNetworkFilterAnalytics,
}) => {
  const t = useTranslate();
  const fm = useContext(IntlContext).formatMessage;
  const distanceOptions = [
    {
      label: `${t("pharm_selection.distance")}: 1 ${t("pharm_selection.mile")}`,
      value: "1",
    },
    {
      label: `${t("pharm_selection.distance")}: ${fm(
        { id: "pharm_selection.miles" },
        { numMiles: 5 }
      )}`,
      value: "5",
    },
    {
      label: `${t("pharm_selection.distance")}: ${fm(
        { id: "pharm_selection.miles" },
        { numMiles: 10 }
      )}`,
      value: "10",
    },
    {
      label: `${t("pharm_selection.distance")}: ${fm(
        { id: "pharm_selection.miles" },
        { numMiles: 25 }
      )}`,
      value: "25",
    },
  ];
  const [showOnlyPreferredInNetwork, setShowOnlyPreferredInNetwork] = useState(
    false
  );
  const [cachedMapRadius, setCachedMapRadius] = useState(0);

  const handlePreferredToggle = () => {
    setShowOnlyPreferredInNetwork(!showOnlyPreferredInNetwork);
    handlePreferredOnlyClick(!showOnlyPreferredInNetwork);
  };

  const handleMapRadiusCaching = (radius: number) => {
    setCachedMapRadius(radius);
  };

  const mailOrderId = "mail-order";
  const mailOrderNameId = `${mailOrderId}${nameIdSuffix}`;
  const mailOrderLabelId = `${mailOrderId}${labelIdSuffix}`;

  const filterRef = useRef<HTMLDivElement>(null);

  const scrollToFilterSection = () => {
    if (filterRef.current) {
      filterRef.current.scrollIntoView();
    }
  };

  return (
    <div className="SelectPharmaciesPage__resultsContainer">
      <div ref={filterRef} className="SelectPharmaciesPage__distanceFilter">
        <div className="ds-u-display--flex ds-u-lg-display--none ds-u-justify-content--center mct-u-width--full">
          <Button
            className="ds-u-margin-right--2 ds-u-display--flex ds-u-align-items--center"
            onClick={(): void => setShowMobileFilter(!showMobileFilter)}
          >
            <img src={filterIcon} alt="" />{" "}
            <span className="ds-u-margin-left--1">
              {t("pharm_selection.filter")}
            </span>
          </Button>
          <Button
            className="ds-c-button--primary-alt ds-u-display--flex ds-u-align-items--center"
            onClick={(): void => {
              setShowMap(!showMap);
              sendTealiumEvent(
                "Find Plans - Add Pharmacy",
                `${showMap ? "Unhide" : "Hide"} Map`
              );
            }}
          >
            <img src={mapIcon} alt="" />
            <span className="ds-u-margin-left--1">
              {!showMap
                ? t("pharm_selection.show_map")
                : t("pharm_selection.hide_map")}
            </span>
          </Button>
        </div>

        {showMobileFilter && (
          <div className="mct-c-help-drawer">
            <HelpDrawer
              heading={t("pharm_selection.filters")}
              headingLevel="2"
              onCloseClick={(): void => {
                setShowMobileFilter(false);
                sendTealiumEvent("Find Plans - Add Pharmacy", "Close Filters");
              }}
              closeButtonText={<CloseIconThin />}
            >
              <>
                <ChoiceList
                  type="radio"
                  name="distance"
                  choices={distanceOptions.map(c => ({
                    ...c,
                    checked: c.value === mobileSearchDistance?.toString(),
                  }))}
                  onChange={(e: SyntheticEvent<HTMLInputElement>): void => {
                    setMobileSearchDistance(Number(e.currentTarget.value));
                  }}
                  label={t("pharm_selection.distance")}
                />
                {isInNetwork && (
                  <>
                    <Choice
                      name="show-only-preferred-in-network-pharmacies-choice"
                      type="checkbox"
                      value="show-only-preferred"
                      checked={showOnlyPreferredInNetwork}
                      onChange={() => {
                        setShowOnlyPreferredInNetwork(
                          !showOnlyPreferredInNetwork
                        );
                      }}
                      label={t(
                        "pharm_selection.in_network.show_only_preferred_in_network_pharmacies"
                      )}
                    />
                    <InNetworkHelperDrawer />
                  </>
                )}
                <div className="SelectPharmaciesPage__mobileFilterActions">
                  <Button
                    className="ds-c-button--primary-alt"
                    onClick={(): void => {
                      if (mobileSearchDistance) {
                        setSearchDistance(mobileSearchDistance);
                        loadPharmacies({
                          radius_miles: mobileSearchDistance,
                          pageNumber: 0,
                          filterByPreferred: showOnlyPreferredInNetwork,
                        });
                        if (inNetworkFilterAnalytics) {
                          inNetworkFilterAnalytics(
                            showOnlyPreferredInNetwork,
                            mobileSearchDistance
                          );
                        }
                      }

                      setShowMobileFilter(false);
                      scrollToFilterSection();
                    }}
                  >
                    {t("pharm_selection.apply")}
                  </Button>
                </div>
              </>
            </HelpDrawer>
          </div>
        )}
        <div className="ds-u-display--none ds-u-lg-display--flex ds-u-align-items--center">
          <p className="ds-u-margin-right--2" id="filter-by-text">
            {t("pharm_selection.filter_by")}:
          </p>
          <CCXPDropdown
            value={searchDistance.toString()}
            name="filter-by"
            aria-labelledby="filter-by-text"
            label={t("pharm_selection.distance")}
            options={distanceOptions}
            onUpdate={(value: string): void => {
              const dist = Number(value);
              setSearchDistance(dist);
              loadPharmacies({
                radius_miles: dist,
                pageNumber: 0,
                filterByPreferred: showOnlyPreferredInNetwork,
              });
              if (!isInNetwork) {
                sendTealiumEvent(
                  "Find Plans - Add Pharmacy",
                  `Results UI: Filter by: ${dist} miles`
                );
              } else if (inNetworkFilterAnalytics) {
                inNetworkFilterAnalytics(showOnlyPreferredInNetwork, dist);
              }
            }}
            hideClear
          />
          {isInNetwork && (
            <>
              <Choice
                className="ds-u-margin-left--3"
                name="show-only-preferred-in-network-pharmacies-choice"
                type="checkbox"
                value="show-only-preferred"
                checked={showOnlyPreferredInNetwork}
                onChange={handlePreferredToggle}
                label={t(
                  "pharm_selection.in_network.show_only_preferred_in_network_pharmacies"
                )}
              />
              <InNetworkHelperDrawer />
            </>
          )}
        </div>
      </div>
      <div className="SelectPharmaciesPage__results">
        <div className="SelectPharmaciesPage__pharmacyOptionsContainer">
          <FormattedResultsMessage
            {...{
              pharmacyResults,
              totalPharmacies,
              previousNameSearch,
              page,
            }}
          />
          {shouldMailOrderBeInResults && (
            <div
              className="SelectPharmaciesPage__grayBottomBorder ds-u-padding-y--1"
              id="MailOrderPharmacyCard"
            >
              <div className="SelectPharmaciesPage__pharmacyCard">
                <div className="ds-u-display--flex">
                  {pharmacyResults &&
                    pharmacyResults.pharmacies.features.length > 0 && (
                      <span className="SelectPharmaciesPage__pharmacyNumber">
                        &nbsp;
                      </span>
                    )}
                  <div className="SelectPharmaciesPage__pharmacyInfo">
                    <h2
                      id={mailOrderNameId}
                      className={`SelectPharmaciesPage__pharmacyCardHeader ${isInNetwork &&
                        "ds-u-margin-bottom--0"}`}
                    >
                      {t("pharm_selection.mail_order_pharm")}
                    </h2>
                    <p className="ds-u-margin--0 SelectPharmaciesPage__pharmacyCard__status-container">
                      {isInNetwork && (
                        <SimplePharamcyNetworkStatus
                          inNetwork={isMailOrderInNetwork}
                          preferred={isMailOrderPreferred}
                        />
                      )}
                    </p>
                    <p className="ds-u-margin--0">
                      {t("pharm_selection.mail_order_pharm_desc")}
                    </p>
                  </div>
                </div>

                <div className="SelectPharmaciesPage__addPharmacy">
                  <Choice
                    type="checkbox"
                    name="include-mail-order-pharmacy"
                    value="mail-order-pharmacy"
                    className="e2e-mail-order-pharmacy"
                    checked={mailOrder}
                    onChange={(): void => {
                      if (
                        !mailOrder &&
                        pharmacies.length >= CONSTANTS.pharmacySelection.max
                      ) {
                        setFooterMessage(
                          fm(
                            {
                              id:
                                "pharmacy_selection_page.max_selection_warning",
                            },
                            { count: CONSTANTS.pharmacySelection.max }
                          )
                        );
                      } else {
                        toggleMailOrderPharmacy(!mailOrder);
                      }
                    }}
                    aria-labelledby={`${mailOrderNameId} ${mailOrderLabelId}`}
                  >
                    <span id={mailOrderLabelId}>
                      {/* Add sr-only comma and space for screen-reader intelligibility */}
                      <span className="ds-u-visibility--screen-reader">{`, `}</span>
                      {mailOrder
                        ? t("pharm_selection.pharmacy_added")
                        : t("pharm_selection.add_pharmacy")}
                    </span>
                  </Choice>
                </div>

                {!!pharmacies.length && (
                  <SkipNav
                    href={`#${pharmacyAnchorTarget}`}
                    onClick={(e): void => {
                      e.preventDefault();
                      document.getElementById(pharmacyAnchorTarget)?.focus();
                    }}
                  >
                    {t("pharmacy_skip_text")}
                  </SkipNav>
                )}
              </div>
            </div>
          )}

          {!loading &&
            pharmacyResults &&
            !pharmacyResults.pharmacies.features.length && (
              <div className="ds-u-padding-y--3 ds-u-padding-left--2 ds-u-md-padding-left--7 ds-u-md-padding-right--3">
                <h2>
                  {isInNetwork
                    ? showOnlyPreferredInNetwork
                      ? t("pharm_selection.in_network.no_preferred_plans_found")
                      : t(
                          "pharm_selection.in_network.no_in_network_plans_found"
                        )
                    : t("pharm_selection.no_pharmacies")}
                </h2>
                <p>{t("pharm_selection.improve_search")}</p>
                <ul className="ds-c-list--bare" role="list">
                  <li role="listitem">
                    <p>{t("pharm_selection.try_different_area")}</p>
                  </li>
                  <li role="listitem">
                    <p>{t("pharm_selection.check_spelling")}</p>
                  </li>
                  {showOnlyPreferredInNetwork && (
                    <li role="listitem">
                      {t(
                        "pharm_selection.in_network.show_standard_in_network_pharmacies"
                      )}
                      .
                      <button
                        type="button"
                        className="ds-c-button ds-u-display--block ds-u-margin-top--1"
                        onClick={handlePreferredToggle}
                      >
                        {t(
                          "pharm_selection.in_network.show_standard_in_network_pharmacies"
                        )}
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            )}

          {pharmacyResults && (
            <ol className="SelectPharmaciesPage__pharmacies" role="list">
              {pharmacyResults.pharmacies.features.map((pharmacy, index) => {
                const { properties } = pharmacy;
                const {
                  npi,
                  name,
                  phone,
                  city,
                  state,
                  street,
                  unit,
                  zipcode,
                } = properties;

                const address = `${normalizeStringCase(street)}${
                  unit ? ` ${normalizeStringCase(unit)}` : ""
                }, ${normalizeStringCase(city)}, ${state} ${zipcode.substring(
                  0,
                  5
                )}`;

                const isSelected = pharmacies.map(p => p.npi).includes(npi);

                const pharmacyNumber =
                  CONSTANTS.pharmacySelection.pageCount * page + (index + 1);

                return (
                  <PharmacyCard
                    key={npi}
                    {...{
                      address,
                      npi,
                      name,
                      phone,
                      pharmacyNumber,
                      pharmacyAnchorTarget,
                      handleToggle: togglePharmacy,
                      preferred: properties.preferred || false,
                      shouldShowSkipNav: !!pharmacies.length,
                      shouldShowNetworkStatus: isInNetwork,
                      index,
                      inNetwork: properties.in_network || false,
                      isSelected,
                    }}
                  />
                );
              })}
            </ol>
          )}
          {!loading &&
            pharmacyResults &&
            pharmacyResults.pharmacies.features.length > 0 && (
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalResults={pharmacyResults.totalResults}
                onPageSelect={(page: number): void => {
                  setPage(page);
                  loadPharmacies({
                    radius_miles: searchDistance,
                    pageNumber: page,
                    filterByPreferred: showOnlyPreferredInNetwork,
                  });
                }}
              />
            )}
        </div>
        {pharmacyResults && showMap && (
          <div className="SelectPharmaciesPage__pharmaciesMap">
            <PharmacySelectionMap
              data={pharmacyResults}
              selectedPharmacies={pharmacies}
              page={page}
              onRadiusCache={handleMapRadiusCaching}
              fallbackRadius={cachedMapRadius}
            />
          </div>
        )}
      </div>
    </div>
  );
};
